/**
 * VariantLinksSection - Shared component for variant commerce links
 *
 * Renders Detail Link and Add to Cart Link fields.
 * Used by all product type expanded content components.
 */

import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { VariantLinksSectionProps } from './types';

const validateUrl = (url: string): boolean => {
  if (!url || url.trim() === '') return true;
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/** The same labelled URL field appears four times across the two layouts. */
const LinkField: React.FC<{
  label: string;
  value: string;
  invalid: boolean;
  helperText: string;
  onChange: (value: string) => void;
}> = ({ label, value, invalid, helperText, onChange }) => {
  const fieldId = useId();

  return (
    <div className="space-y-1">
      <Label htmlFor={fieldId} className="text-xs font-medium text-muted-foreground">{label}</Label>
      <Input
        id={fieldId}
        placeholder="https://..."
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        aria-invalid={invalid}
      />
      {invalid && <p className="text-xs text-destructive">{helperText}</p>}
    </div>
  );
};

export const VariantLinksSection: React.FC<VariantLinksSectionProps> = ({
  variant,
  onVariantLinkChange,
  onVariantAddToCartLinkChange,
  layout = 'vertical',
}) => {
  const { t } = useTranslation();

  const linkValue = variant.commerce?.link || '';
  const addToCartValue = variant.commerce?.addToCartLink || '';
  const linkError = linkValue.length > 0 && !validateUrl(linkValue);
  const addToCartError = addToCartValue.length > 0 && !validateUrl(addToCartValue);
  const invalidUrlText = t('PAGES.NEW_PRODUCT.V3.VALIDATION.INVALID_URL');

  const detailField = (
    <LinkField
      label={t('PAGES.NEW_PRODUCT.V2.VARIANT_TABLE.DETAIL_LINK')}
      value={linkValue}
      invalid={linkError}
      helperText={invalidUrlText}
      onChange={(value) => onVariantLinkChange?.(variant.uuid, value)}
    />
  );

  const cartField = (
    <LinkField
      label={t('PAGES.NEW_PRODUCT.V2.VARIANT_TABLE.ADD_TO_CART_LINK')}
      value={addToCartValue}
      invalid={addToCartError}
      helperText={invalidUrlText}
      onChange={(value) => onVariantAddToCartLinkChange?.(variant.uuid, value)}
    />
  );

  if (layout === 'horizontal') {
    return (
      <div className="flex gap-4">
        <div className="flex-1">{detailField}</div>
        <div className="flex-1">{cartField}</div>
      </div>
    );
  }

  // Vertical layout (default)
  return (
    <div className="space-y-3">
      {detailField}
      {cartField}
    </div>
  );
};

export default VariantLinksSection;
